import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react'
import type { CentreDeVille } from './Carte'

/**
 * MapLibre, chargé **à la demande** — et c'est une décision de performance, pas de style.
 *
 * Importée normalement, la bibliothèque pèse 900 Ko dans le bundle initial, que le service worker
 * précharge intégralement (`globPatterns`). Sur la cible du §13 de la spec — un réseau 3G — cela
 * fait une trentaine de secondes avant le premier affichage, pour une carte que la majorité des
 * visiteurs ne verra jamais : elle ne sert qu'au propriétaire qui publie, et au bas d'une fiche.
 *
 * Le découpage la sort du chemin critique. Le chunk est en outre exclu du préchargement dans
 * `vite.config.ts` et servi en `StaleWhileRevalidate` : il arrive au premier usage, puis reste en
 * cache.
 */
const SelecteurDePointCharge = lazy(() =>
  import('./Carte').then((module) => ({ default: module.SelecteurDePoint })),
)

const CarteApprocheeChargee = lazy(() =>
  import('./Carte').then((module) => ({ default: module.CarteApprochee })),
)

const CarteRechercheChargee = lazy(() =>
  import('./Carte').then((module) => ({ default: module.CarteRecherche })),
)

function Attente() {
  return <div className="carte__toile carte__toile--attente" aria-hidden="true" />
}

/**
 * Ne monte son contenu qu'une fois la zone approchée de l'écran.
 *
 * **Le découpage ne suffisait pas.** Sur la fiche d'annonce, la carte de situation est le
 * dernier bloc de la page, et elle était pourtant montée au premier rendu : les 219 ko de
 * MapLibre partaient en même temps que la première photo. Mesuré en 3G rapide émulée le
 * 07/09/2026 : **LCP à 13,9 s**, pour une carte que personne n'a encore fait défiler.
 *
 * `IntersectionObserver` avec une marge de 400 px : la bibliothèque part quand la carte
 * approche, donc elle est là quand on y arrive, sans jamais disputer la bande passante à ce
 * qu'on regarde d'abord.
 *
 * Sans `IntersectionObserver` — un navigateur ancien —, on monte tout de suite : mieux vaut une
 * page lourde qu'une carte absente.
 */
function QuandVisible({ children }: { children: ReactNode }) {
  const ancre = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const element = ancre.current

    if (visible || element === null) {
      return
    }

    const observateur = new IntersectionObserver(
      (entrees) => {
        if (entrees.some((entree) => entree.isIntersecting)) {
          setVisible(true)
        }
      },
      { rootMargin: '400px' },
    )

    observateur.observe(element)

    return () => observateur.disconnect()
  }, [visible])

  return <div ref={ancre}>{visible ? children : <Attente />}</div>
}

export function SelecteurDePoint(props: {
  latitude: number | null
  longitude: number | null
  centre?: CentreDeVille
  onDeplacer: (latitude: number, longitude: number) => void
}) {
  return (
    <Suspense fallback={<Attente />}>
      <SelecteurDePointCharge {...props} />
    </Suspense>
  )
}

export function CarteApprochee(props: {
  latitude: number
  longitude: number
  rayonMetres?: number
}) {
  return (
    <QuandVisible>
      <Suspense fallback={<Attente />}>
        <CarteApprocheeChargee {...props} />
      </Suspense>
    </QuandVisible>
  )
}

/**
 * Carte de recherche, chargee a la demande comme les deux autres.
 *
 * Sur l'ecran de recherche, la carte est masquee par defaut en mobile : le visiteur qui reste en
 * liste ne telecharge donc jamais MapLibre.
 */
export function CarteRecherche(props: {
  punaises: { id: string; latitude: number; longitude: number; rentAllInclusive: number }[]
  cadrage?: { south: number; west: number; north: number; east: number }
  selection?: string | null
  centre?: CentreDeVille
  onCadrageChange: (cadrage: { south: number; west: number; north: number; east: number }) => void
  onOuvrir: (id: string) => void
}) {
  return (
    <Suspense fallback={<Attente />}>
      <CarteRechercheChargee {...props} />
    </Suspense>
  )
}
